/**
 * ContentMixPie.jsx — small SVG pie chart for the daily content-mix
 * summary inside the Clock View "Σύνοψη Ημέρας" card.
 *
 * v2.13.19 (2026-02). Pure presentation — feed it the breakdown
 * (music_pct, speech_pct, mixed_pct, noise_pct, ads_estimate_pct,
 * coverage_pct, samples_total). Renders nothing when samples_total=0.
 *
 * The visible slices ALWAYS sum to 100% (we normalise across the
 * evaluable categories — music + speech + mixed + noise). Coverage is
 * displayed as a small badge underneath so the operator knows what
 * fraction of the day's classifier samples produced a usable result.
 */
import React from 'react';

const PALETTE = {
  music:  '#4ade80',
  speech: '#fbbf24',
  mixed:  '#60a5fa',
  noise:  '#a78bfa',
};

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const [x1, y1] = polar(cx, cy, r, startDeg);
  const [x2, y2] = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
};

const ContentMixPie = ({ data, title = 'Σύνθεση Ημέρας', testId = 'content-mix-pie' }) => {
  if (!data || !data.samples_total) return null;

  // Build slices that ALWAYS sum to 100% (visible categories only).
  // Drop zero-value categories so the pie isn't polluted with tiny
  // hairlines.
  const visible = [
    { key: 'music',  label: 'Μουσική',       value: data.music_pct,  hex: PALETTE.music  },
    { key: 'mixed',  label: 'Μεικτό (DJ/Διαφ.)', value: data.mixed_pct,  hex: PALETTE.mixed  },
    { key: 'speech', label: 'Ομιλία',        value: data.speech_pct, hex: PALETTE.speech },
    { key: 'noise',  label: 'Άλλο',          value: data.noise_pct,  hex: PALETTE.noise  },
  ].filter((s) => s.value > 0);

  const total = visible.reduce((sum, s) => sum + s.value, 0) || 1;

  // Compute angles
  let runningDeg = 0;
  const slices = visible.map((s) => {
    const span = (s.value / total) * 360;
    const slice = { ...s, startDeg: runningDeg, endDeg: runningDeg + span };
    runningDeg += span;
    return slice;
  });

  const W = 220;
  const cx = W / 2;
  const cy = W / 2;
  const R = 88;

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-lg p-4"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-[12px] font-bold uppercase tracking-wide text-white/85">
          {title}
        </h4>
      </div>
      <div className="flex flex-col items-center gap-3">
        <svg width={W} height={W} viewBox={`0 0 ${W} ${W}`}>
          {slices.length === 1 ? (
            // Single-slice = full circle. SVG arc can't draw 360°
            // in one path, so render a plain <circle> instead.
            <circle
              cx={cx} cy={cy} r={R}
              fill={slices[0].hex}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth="1"
            />
          ) : (
            slices.map((s) => (
              <path
                key={s.key}
                d={arcPath(cx, cy, R, s.startDeg, s.endDeg)}
                fill={s.hex}
                stroke="rgba(0,0,0,0.3)"
                strokeWidth="1"
                data-testid={`content-mix-pie-slice-${s.key}`}
              />
            ))
          )}
          {/* Center hole (donut style) */}
          <circle cx={cx} cy={cy} r={R * 0.45} fill="#0f172a" />
          <text
            x={cx} y={cy - 2}
            textAnchor="middle"
            fontSize="11"
            fill="rgba(255,255,255,0.45)"
            letterSpacing="0.5"
          >
            ΣΥΝΟΛΟ
          </text>
          <text
            x={cx} y={cy + 14}
            textAnchor="middle"
            fontSize="14"
            fill="rgba(255,255,255,0.85)"
            fontWeight="700"
          >
            {data.samples_total.toLocaleString()}
          </text>
        </svg>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 w-full">
          {slices.map((s) => (
            <div
              key={s.key}
              className="flex items-center gap-2 text-[11px]"
              data-testid={`content-mix-pie-legend-${s.key}`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: s.hex }}
              />
              <span className="text-white/70 truncate flex-1">{s.label}</span>
              <span
                className="tabular-nums font-semibold"
                style={{ color: s.hex }}
              >
                {s.value.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
        {data.ads_estimate_pct > 0 && (
          <div
            className="text-[10px] text-red-300/80 italic mt-1 text-center"
            title="Heuristic: consecutive speech+music runs of 20-40s"
          >
            ~ {data.ads_estimate_pct.toFixed(1)}% εκτιμώμενες διαφημίσεις
            (από Mixed)
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentMixPie;
